import type { ReactNode } from "react";
import { Text, type Key } from "react-aria-components";
import type { Size } from "../helpers/helpers.js";
import { SelectBase } from "./selectBase.js";

export interface DescriptionSelectProps<T extends Key | null> {
  leftAccessory?: ReactNode;
  label?: string;
  isLabelHidden?: boolean;
  isDisabled?: boolean;
  isInvalid?: boolean;
  size?: typeof Size.SMALL | typeof Size.MEDIUM;
  /** Stretch the trigger button to fill its container instead of sizing to content. */
  isFullWidth?: boolean;
  /** Optional className applied to the options popover. Use to constrain the menu width. */
  popoverClassName?: string;
  /** Maximum description lines shown for every option in this dropdown. */
  descriptionLineLimit: 1 | 2;
  placeholder?: ReactNode;
  options: readonly DescriptionSelectOption<T>[];
  disabledValues?: readonly T[];
  value: T | null;
  onChange: (value: T) => void;
}

export interface DescriptionSelectOption<T extends Key | null> {
  value: T;
  icon?: ReactNode;
  label: string;
  description: string;
}

/** Single-select menu for options that need secondary explanatory text. */
export const DescriptionSelect = <T extends Key | null>(
  props: DescriptionSelectProps<T>,
) => {
  const descriptionClassName =
    props.descriptionLineLimit === 1
      ? "mt-0.5 block truncate text-xs leading-4 font-normal text-muted-foreground"
      : "mt-0.5 line-clamp-2 text-xs leading-4 font-normal text-muted-foreground";

  return (
    <SelectBase
      {...props}
      renderOption={(option) => (
        <div className="min-w-0 flex-1">
          <Text
            slot="label"
            className="block truncate text-sm leading-5 font-medium text-foreground group-selected:font-semibold"
          >
            {option.label}
          </Text>
          <Text slot="description" className={descriptionClassName}>
            {option.description}
          </Text>
        </div>
      )}
    />
  );
};
